import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NEW_GOOGLE_USER_KEY } from "@/lib/authRedirect";

interface GoogleOAuthChoiceProps {
  userName: string;
  onDismiss: () => void;
}

const GoogleOAuthChoice = ({ userName, onDismiss }: GoogleOAuthChoiceProps) => {
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState(false);

  const handleStayCustomer = () => {
    setChoosing(true);
    sessionStorage.removeItem(NEW_GOOGLE_USER_KEY);
    onDismiss();
  };

  const handleBecomeReseller = () => {
    sessionStorage.removeItem(NEW_GOOGLE_USER_KEY);
    onDismiss();
    navigate("/reseller?mode=register");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
        <div className="bg-gradient-to-r from-accent-400 to-accent-500 px-6 py-6 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 mx-auto mb-3">
            <i className="ri-google-fill text-white text-2xl"></i>
          </div>
          <h2 className="text-white font-extrabold text-xl mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
            Welcome, {userName}!
          </h2>
          <p className="text-accent-100 text-sm">You signed in with Google. What brings you here?</p>
        </div>

        <div className="p-6 space-y-4">
          <button
            onClick={handleStayCustomer}
            disabled={choosing}
            className="w-full bg-white border-2 border-foreground-200 hover:border-primary-400 rounded-xl p-5 text-left cursor-pointer transition-all hover:bg-primary-50/30 group disabled:opacity-50"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 group-hover:bg-primary-200 transition-colors flex-shrink-0">
                <i className="ri-shopping-cart-line text-primary-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-foreground-900 text-sm mb-1">Just shopping</h3>
                <p className="text-foreground-500 text-xs leading-relaxed">
                  Get member pricing, faster checkout, and order history. Buy data for yourself or others — no strings attached.
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={handleBecomeReseller}
            disabled={choosing}
            className="w-full bg-accent-50 border-2 border-accent-200 hover:border-accent-400 rounded-xl p-5 text-left cursor-pointer transition-all hover:bg-accent-100/50 group disabled:opacity-50"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-200 group-hover:bg-accent-300 transition-colors flex-shrink-0">
                <i className="ri-user-star-line text-accent-700 text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-foreground-900 text-sm mb-1">Become a reseller</h3>
                <p className="text-foreground-500 text-xs leading-relaxed">
                  Set your own markup and keep it on every sale. Get a personal dashboard, referral link, and wallet.
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="px-6 pb-6">
          <p className="text-center text-xs text-foreground-400">
            You can always become a reseller later from your account settings.
          </p>
        </div>
      </div>
    </div>
  );
};

// Shows the choice ONLY to brand-new Google users, once
export function useGoogleOAuthChoice(
  userId: string | undefined,
  _userEmail: string | undefined,
  userName: string | undefined
): { showChoice: boolean; ChoiceComponent: React.ReactNode | null } {
  const [showChoice, setShowChoice] = useState(false);

  useEffect(() => {
    if (!userId) return;
    if (sessionStorage.getItem(NEW_GOOGLE_USER_KEY)) setShowChoice(true);
  }, [userId]);

  const ChoiceComponent = showChoice && userId
    ? <GoogleOAuthChoice userName={userName || "there"} onDismiss={() => setShowChoice(false)} />
    : null;

  return { showChoice, ChoiceComponent };
}

export default GoogleOAuthChoice;